'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Lock, Vote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { verVotacion, votarVotacion } from '@/lib/actions';
import type { VotacionPublica } from '@/lib/types';

/**
 * La papeleta de voto dentro de la carta del buzón. Pide el estado a la CF
 * (`ver`): el hermano no puede leer el documento de la votación, que lleva los
 * votos de todos. Si ya está cerrada, no deja votar.
 */
export function VotacionCarta({ hermandadId, votacionId }: { hermandadId: string; votacionId: string }) {
  const [cargando, setCargando] = useState(true);
  const [v, setV] = useState<VotacionPublica | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elegida, setElegida] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    const r = await verVotacion(hermandadId, votacionId);
    setV(r.votacion);
    setError(r.success ? null : (r.message || 'No se pudo cargar la votación.'));
    setCargando(false);
  };

  useEffect(() => { cargar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hermandadId, votacionId]);

  const votar = async () => {
    if (!elegida || !v) return;
    setEnviando(true);
    const r = await votarVotacion(hermandadId, votacionId, elegida);
    if (r.success) {
      setV({ ...v, miVoto: elegida });
      setError(null);
    } else {
      // Puede que la hayan cerrado mientras tanto: se recarga el estado real.
      setError(r.message || 'No se pudo votar.');
      await cargar();
      setError(r.message || 'No se pudo votar.');
    }
    setEnviando(false);
  };

  const textoDe = (id: string | null) => v?.opciones.find((o) => o.id === id)?.texto || '';

  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary"><Vote className="h-4 w-4" /> Tu voto</p>

      {cargando ? (
        <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : !v ? (
        <p className="text-sm text-rose-300">{error || 'Esta votación ya no existe.'}</p>
      ) : v.miVoto ? (
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Has votado: <strong>{textoDe(v.miVoto)}</strong>
          </p>
          <p className="text-xs text-muted-foreground">
            {v.estado === 'cerrada' ? 'La votación ya ha finalizado.' : 'Tu voto está registrado. No se puede cambiar.'}
          </p>
        </div>
      ) : v.estado !== 'abierta' ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" /> La votación está cerrada. Ya no se puede votar.
        </p>
      ) : !v.puedeVotar ? (
        <p className="text-sm text-muted-foreground">No estabas en la lista de hermanos cuando se abrió esta votación.</p>
      ) : (
        <div className="space-y-2">
          {v.opciones.map((o) => (
            <button
              key={o.id}
              onClick={() => setElegida(o.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm transition-colors',
                elegida === o.id ? 'border-primary bg-primary/15 font-semibold' : 'border-white/10 bg-background/40 hover:border-white/25',
              )}
            >
              <span className={cn('grid h-4 w-4 shrink-0 place-items-center rounded-full border', elegida === o.id ? 'border-primary' : 'border-white/30')}>
                {elegida === o.id && <span className="h-2 w-2 rounded-full bg-primary" />}
              </span>
              {o.texto}
            </button>
          ))}
          {error && <p className="text-xs text-rose-300">{error}</p>}
          <Button className="w-full gap-2" disabled={!elegida || enviando} onClick={votar}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Vote className="h-4 w-4" />} Votar
          </Button>
          <p className="text-center text-[0.7rem] text-muted-foreground">Solo se puede votar una vez.</p>
        </div>
      )}
    </div>
  );
}
